/**
 * The phone's pre-use checklist (api#1209): a published template's items,
 * grouped by section, each answered Pass / Fail / N/A with an optional note.
 *
 * It records answers and nothing else. The overall result is the SERVER's —
 * any critical fail is `fail`, any other fail `needs_repair` — so no status is
 * computed or shown here before the save.
 */
import React, { useState } from 'react';
import type { PreuseResult, PreuseTemplateItem } from '../../types/preuse';
import { groupItemsBySection } from '../../lib/preuse';

export interface PreuseAnswer {
  result: PreuseResult | null;
  note: string;
}

const RESULTS: Array<{ value: PreuseResult; label: string; on: string }> = [
  { value: 'pass', label: 'Pass', on: 'bg-green-500 text-white' },
  { value: 'fail', label: 'Fail', on: 'bg-red-500 text-white' },
  { value: 'na', label: 'N/A', on: 'bg-slate-500 text-white' },
];

export function PreuseChecklist({
  items,
  answers,
  onChange,
  missing,
}: {
  items: PreuseTemplateItem[];
  answers: Record<string, PreuseAnswer>;
  onChange: (itemKey: string, next: PreuseAnswer) => void;
  /** item_keys the hand tried to save without answering. */
  missing: Set<string>;
}) {
  const [noteOpen, setNoteOpen] = useState<Record<string, boolean>>({});
  const groups = groupItemsBySection(items);
  return (
    <div className="space-y-4" data-testid="preuse-checklist">
      {groups.map((g) => (
        <fieldset key={g.section} className="space-y-2">
          <legend className="text-xs font-bold uppercase tracking-[0.1em] text-fieldiq-muted mb-1">{g.section}</legend>
          {g.items.map((it) => {
            const a = answers[it.itemKey] ?? { result: null, note: '' };
            const isMissing = missing.has(it.itemKey);
            const showNote = noteOpen[it.itemKey] || a.note !== '';
            return (
              <div
                key={it.itemKey}
                data-testid={`preuse-item-${it.itemKey}`}
                className={`rounded-lg border bg-fieldiq-dark p-3 ${isMissing ? 'border-red-500' : 'border-fieldiq-border'}`}
              >
                <p className="text-sm text-fieldiq-text">
                  {it.label}
                  {it.critical && (
                    <span className="ml-2 inline-flex rounded-fi-pill border border-black/20 bg-fi-signal-red px-1.5 py-0.5 align-middle text-[0.6rem] font-black uppercase leading-none text-fi-signal-ink">
                      Critical
                    </span>
                  )}
                  {it.required && <span className="ml-1 text-xs text-fieldiq-muted" aria-label="required">*</span>}
                </p>
                {it.helpText && <p className="mt-1 text-xs text-fieldiq-muted">{it.helpText}</p>}
                <div className="mt-2 grid grid-cols-3 gap-2" role="group" aria-label={it.label}>
                  {RESULTS.map((r) => (
                    <button
                      key={r.value}
                      type="button"
                      aria-pressed={a.result === r.value}
                      onClick={() => onChange(it.itemKey, { ...a, result: r.value })}
                      className={`min-h-[40px] rounded-lg text-sm font-medium transition ${a.result === r.value ? r.on : 'bg-fieldiq-card text-fieldiq-text'}`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
                {isMissing && <p className="mt-1 text-xs text-fieldiq-text">Answer this item — N/A is an answer.</p>}
                {showNote ? (
                  <textarea
                    rows={2}
                    aria-label={`Note for ${it.label}`}
                    value={a.note}
                    maxLength={1000}
                    onChange={(e) => onChange(it.itemKey, { ...a, note: e.target.value })}
                    placeholder="Note (optional)"
                    className="mt-2 w-full resize-none rounded-lg border border-fieldiq-border bg-fieldiq-card px-3 py-2 text-sm outline-none focus:border-fieldiq-accent"
                  />
                ) : (
                  <button
                    type="button"
                    className="mt-2 text-xs font-semibold text-fieldiq-muted underline underline-offset-2"
                    onClick={() => setNoteOpen((o) => ({ ...o, [it.itemKey]: true }))}
                  >
                    Add a note
                  </button>
                )}
              </div>
            );
          })}
        </fieldset>
      ))}
    </div>
  );
}
